import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import Logo from '../../core/components/Logo.tsx';
import { login } from '../../core/utils/authentication.ts';
import { FORGOT_PATH, PROVIDER_LOGIN_PATH, SETUP_MFA_PATH } from '../constants.ts';
import { Controller, useForm } from 'react-hook-form';
import FormField from '../../core/components/FormField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import Button from '../../core/components/Button.tsx';
import AuthPage from '../components/AuthPage.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { faFingerprint, faKey, faMobile } from '@fortawesome/pro-regular-svg-icons';
import { canSkipMfa, getPasskeyCredentials } from '../../users/utils.ts';
import { useMutation, useQuery } from '@apollo/client';
import { GET_MFA_DEVICE_AUTHENTICATION_DATA } from '../../users/queries.ts';
import { MFA_DEVICE_LOGIN } from '../queries.ts';
import classnames from '../../core/utils/classnames.tsx';
import IntegerField from '../../core/components/Fields/IntegerField.tsx';
import { safeInternalUrl, url } from '../../core/utils/link.ts';
import { useApplicationContext } from '../../core/contexts/ApplicationContext.tsx';

const PasskeyIcon = withIcon(faFingerprint);
const AuthenticatorAppIcon = withIcon(faMobile);
const RecoveryCodeIcon = withIcon(faKey);

const FORM_DEFAULT_VALUES = {
    email: '',
    password: '',
};

const LoginFormCard = ({ form, onSubmit, nextLocation }) => {
    const { t } = useTranslation('user');

    const formErrors = form.formState.errors;

    return (
        <>
            <AuthPage.Card>
                <AuthPage.Title>{t('login.title')}</AuthPage.Title>

                {formErrors.root && <AuthPage.Error>{formErrors.root.message}</AuthPage.Error>}

                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
                    <Controller
                        name="email"
                        control={form.control}
                        rules={{
                            required: t('login.form.emailRequired'),
                            pattern: {
                                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: t('login.form.emailInvalid'),
                            },
                        }}
                        render={({ field }) => (
                            <FormField label={t('login.form.email')} error={formErrors.email} required>
                                <StringField {...field} placeholder={t('login.form.emailPlaceholder')} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="password"
                        control={form.control}
                        rules={{
                            required: t('login.form.passwordRequired'),
                        }}
                        render={({ field }) => (
                            <FormField label={t('login.form.password')} error={formErrors.password} required>
                                <StringField
                                    {...field}
                                    type="password"
                                    placeholder={t('login.form.passwordPlaceholder')}
                                />
                            </FormField>
                        )}
                    />

                    <div className="text-xs text-right -mt-1 -mb-2">
                        <Link to={FORGOT_PATH} className="text-content-brand-default hover:text-content-brand-hover">
                            {t('login.forgotLink')}
                        </Link>
                    </div>

                    <div className="mt-4">
                        <Button variant="primary" className="w-full" loading={form.formState.isSubmitting}>
                            {t('login.form.submitButton')}
                        </Button>
                    </div>
                </form>
            </AuthPage.Card>

            <div className="text-xs text-center">
                <Link
                    to={url(PROVIDER_LOGIN_PATH, undefined, { search: { next: nextLocation } })}
                    className="text-content-brand-default hover:text-content-brand-hover"
                >
                    {t('login.withSSO')}
                </Link>
            </div>
        </>
    );
};

const WebauthnForm = ({ device, onContinue }: any) => {
    const { t } = useTranslation('user');

    const { data } = useQuery(GET_MFA_DEVICE_AUTHENTICATION_DATA, { variables: { deviceType: 'webauthn' } });
    const options = data?.mfaDeviceAuthenticationData?.options;

    const handleWebauthnClick = async () => {
        const attestationCredential: any = await getPasskeyCredentials(options);
        return onContinue({ payload: attestationCredential });
    };

    return (
        <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-content-strong">{t(`login.mfa.${device.deviceType}`)}</h3>

            <MfaDeviceButton device={device} className="pointer-events-none" />

            <Button variant="primary" onClick={handleWebauthnClick} className="my-4">
                {t('login.mfa.continueButton')}
            </Button>
        </div>
    );
};

const TotpForm = ({ device, onContinue }: any) => {
    const { t } = useTranslation('user');
    const [token, setToken] = useState('');

    return (
        <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-content-strong">{t(`login.mfa.${device.deviceType}`)}</h3>

            <MfaDeviceButton device={device} className="pointer-events-none" />

            <div className="bg-surface-medium rounded p-4 my-4">
                <FormField label={t('login.mfa.totpLabel')} required>
                    <IntegerField
                        className="max-w-36 tabular-nums tracking-widest"
                        placeholder="_ _ _ _ _ _"
                        maxLength={6}
                        autoComplete="one-time-code"
                        autoFocus
                        value={token}
                        onValueChange={setToken}
                    />
                </FormField>
            </div>

            <Button
                variant="primary"
                onClick={() => onContinue({ payload: { token } })}
                disabled={!token || token.length < 6}
            >
                {t('login.mfa.continueButton')}
            </Button>
        </div>
    );
};

const StaticForm = ({ device, onContinue }: any) => {
    const { t } = useTranslation('user');
    const [token, setToken] = useState('');

    return (
        <div className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold text-content-medium">{t(`login.mfa.${device.deviceType}`)}</h3>

            <MfaDeviceButton device={device} className="pointer-events-none" />

            <div className="bg-surface-medium rounded p-4 my-4">
                <FormField label={t('login.mfa.staticLabel')} required>
                    <StringField
                        className="max-w-36 tracking-widest"
                        autoFocus
                        value={token}
                        onValueChange={setToken}
                    />
                </FormField>
            </div>

            <Button variant="primary" onClick={() => onContinue({ payload: { token } })} disabled={!token}>
                {t('login.mfa.continueButton')}
            </Button>
        </div>
    );
};

const MfaDeviceButton = ({
    device,
    className,
    ...props
}: React.ComponentPropsWithRef<typeof Button> & { device: any }) => {
    const { t, i18n } = useTranslation('user');

    return (
        <Button key={device.deviceId} className={classnames('flex gap-2 w-full items-center', className)} {...props}>
            <span className="block px-1 text-content-weak">
                {device.deviceType === 'webauthn' ? (
                    <PasskeyIcon className="w-4 h-4 text-content-medium" />
                ) : device.deviceType === 'totp' ? (
                    <AuthenticatorAppIcon className="w-4 h-4 text-content-medium" />
                ) : (
                    <RecoveryCodeIcon className="w-4 h-4 text-content-medium" />
                )}
            </span>

            <span className="flex flex-col text-left py-0.5">
                <span className="text-sm font-medium">{device.name}</span>
                <span className="text-content-medium text-sm font-normal">
                    {t('login.mfa.twoFactorMethodDeviceDescription', {
                        lastUsedAt: device.lastUsedAt
                            ? new Date(device.lastUsedAt).toLocaleString(i18n.language)
                            : t('login.mfa.neverUsed'),
                    })}
                </span>
            </span>
        </Button>
    );
};

const MfaDevicesCard = ({ selectedDevice, onSelectedDeviceChange, onSubmit, devices }) => {
    const { t } = useTranslation('user');

    const deviceGroups = devices.reduce((acc, device) => {
        if (!acc[device.deviceType]) {
            acc[device.deviceType] = [];
        }
        acc[device.deviceType].push(device);
        return acc;
    }, {});

    const sortedDeviceGroups = Object.keys(deviceGroups).sort((a, b) => {
        const order = ['webauthn', 'totp', 'static'];
        return order.indexOf(a) - order.indexOf(b);
    });

    return (
        <AuthPage.Card>
            <AuthPage.Title>{t('login.mfa.title')}</AuthPage.Title>

            <div className="flex flex-col gap-6">
                {!selectedDevice ? (
                    sortedDeviceGroups.map((deviceType) => (
                        <div key={deviceType}>
                            <div className="flex flex-col gap-2">
                                <h3 className="text-sm font-medium text-content-strong">
                                    {t(`login.mfa.${deviceType}`)}
                                </h3>
                                {deviceGroups[deviceType]?.map((device) => (
                                    <MfaDeviceButton
                                        key={device.deviceId}
                                        device={device}
                                        onClick={() => onSelectedDeviceChange(device)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="flex flex-col gap-2">
                        {selectedDevice.deviceType === 'webauthn' ? (
                            <WebauthnForm device={selectedDevice} onContinue={onSubmit} />
                        ) : selectedDevice.deviceType === 'totp' ? (
                            <TotpForm device={selectedDevice} onContinue={onSubmit} />
                        ) : (
                            <StaticForm device={selectedDevice} onContinue={onSubmit} />
                        )}

                        <Button variant="ghost" className="w-full" onClick={() => onSelectedDeviceChange(null)}>
                            {t('login.mfa.backToDevicesButton')}
                        </Button>
                    </div>
                )}
            </div>
        </AuthPage.Card>
    );
};

const LoginPage = (props) => {
    const { loggedIn, user } = props;

    const { refetchUser } = useApplicationContext();

    const { t } = useTranslation('user');

    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);

    const nextLocation = safeInternalUrl(searchParams.get('next'));

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });

    const [mfaSelectedDevice, setMfaSelectedDevice] = useState(null);
    const [mfaDevices, setMfaDevices] = useState([]);

    const handleFormSubmit = async ({ email, password }) => {
        try {
            const response = await login(email, password);

            if (response?.success) {
                const loggedInUser = (await refetchUser())?.data?.viewer;

                // mfa is required if company has mfa enabled or user has mfa device
                if (response.mfaRequired && (response.mfaDevices || []).length > 0) {
                    setMfaDevices(response.mfaDevices || []);
                    if (response.mfaDevice) {
                        setMfaSelectedDevice(response.mfaDevice);
                    }
                } else {
                    if (!canSkipMfa(loggedInUser)) {
                        // always go to setup path if no devices as we want users to use 2FA
                        window.location.href = url(SETUP_MFA_PATH, undefined, { search: { next: nextLocation } });
                    } else {
                        window.location.href = nextLocation;
                    }
                }
            } else if (response?.throttled) {
                form.setError('root', { type: 'throttled', message: t('login.throttledLoginText') });
            } else if (response?.error) {
                if (response?.error?.includes('SSO is required')) {
                    form.setError('root', { type: 'ssoRequired', message: t('login.ssoRequiredText') });
                } else {
                    form.setError('root', {
                        type: 'unexpectedError',
                        message: t('login.unexpectedErrorText', { error: response.error }),
                    });
                }
            } else {
                // if no error and success === false -> wrong credentials
                form.setError('root', { type: 'wrongCredentials', message: t('login.wrongCredentialsText') });
            }
        } catch (error) {
            form.setError('root', {
                type: 'unexpectedError',
                message: t('login.unexpectedErrorText', { error: error.message }),
            });
        }
    };

    const [mfaDeviceLogin] = useMutation(MFA_DEVICE_LOGIN);
    const handleMfaSubmit = async ({ payload }) => {
        try {
            const response = await mfaDeviceLogin({
                variables: {
                    deviceId: mfaSelectedDevice.deviceId,
                    payload,
                },
            });

            if (response.data.mfaDeviceLogin.success) {
                window.location.href = nextLocation;
            } else {
                form.setError('root', { type: 'wrongCredentials', message: t('login.wrongCredentialsText') });
            }
        } catch (error) {
            form.setError('root', { type: 'wrongCredentials', message: t('login.wrongCredentialsText') });
        }
    };

    useEffect(() => {
        if (!loggedIn || !user) return;
        navigate(nextLocation);
    }, [loggedIn, user]);

    return (
        <AuthPage>
            <AuthPage.Content>
                <Logo className="w-32 h-auto" />

                {mfaDevices.length === 0 ? (
                    <LoginFormCard form={form} onSubmit={handleFormSubmit} nextLocation={nextLocation} />
                ) : (
                    <MfaDevicesCard
                        devices={mfaDevices}
                        selectedDevice={mfaSelectedDevice}
                        onSelectedDeviceChange={setMfaSelectedDevice}
                        onSubmit={handleMfaSubmit}
                    />
                )}
            </AuthPage.Content>

            <AuthPage.Footer />
        </AuthPage>
    );
};

export default LoginPage;
